import React, { useRef, useEffect } from "react";
import { Form, Input, Row, Col, DatePicker, Select } from "antd";
import styled from "@emotion/styled";
import moment from "moment";
import { momentDate } from "utils/Date";
import {
  NON_EMPTY_REGEX,
  NON_CHARACTER,
  VALIDATE_CHARACTER,
  TIMEZONELIST,
  countryList,
} from "common/Constants";
import { connect } from "react-redux";
import { getHolidayList } from "redux/selectors";
import { useTranslation } from "react-i18next";

const Styleddiv = styled.div`
  .ant-form-item-label > label {
    font-weight: bold;
  }
`;

const EditInformation = ({
  title,
  details,
  setValues,
  viewOnly,
  setsubmit,
  holidayList,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    const {
      name = title === "Add Location"
        ? details.location_name
        : title === "holiday"
        ? details.holiday_name
        : title === "break"
        ? details.breakName
        : details.department_name,
      location_latitude = details.latitude,
      location_longitude = details.longitude,
      start_date = details.start_date,
      end_date = details.end_date,
      country = details?.country,
      usertimezone = details?.usertimezone,
      description = details?.description,
    } = details;

    form.setFieldsValue({
      name: name,
      location_latitude: location_latitude,
      location_longitude: location_longitude,
      start_date: start_date ? moment(start_date) : null,
      end_date: end_date ? moment(end_date) : null,
      country: country,
      usertimezone: usertimezone,
      description: description,
    });

    setValues(form.getFieldValue());
  }, [details]);

  const containerRef = useRef(null);

  const validateDateRange = (date) => {
    const endDate = moment(form.getFieldValue("end_date"));
    if (endDate < date) {
      form.setFieldsValue({ end_date: date });
      setValues((prevValues) => ({
        ...prevValues,
        end_date: date.format("YYYY-MM-DD"),
      }));
    }
  };

  const handleFormValuesChange = (changedValues, allValues) => {
    setsubmit(true);
    setValues(allValues);
  };

  return (
    <Styleddiv>
      <Form
        form={form}
        name="AddDepartment"
        layout="vertical"
        disabled={viewOnly}
        initialValues={{
          name: "",
          latitude: "",
          longtitude: "",
          start_date: momentDate(new Date()),
          end_date: "",
          usertimezone: "",
          country: "",
          description: "",
        }}
        onValuesChange={handleFormValuesChange}
      >
        <div className="styled-card">
          {/* Country */}
          {title === "Add Location" && (
            <Form.Item
              name="country"
              label={t("Country")}
              rules={[{ required: true, message: t("Please select country") }]}
            >
              <Select showSearch placeholder={t("Search to Select")}>
                {countryList?.map((country, index) => (
                  <Select.Option key={index} value={country}>
                    {country}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Name */}
          <Form.Item
            label={
              title === "Add Location"
                ? t("Location Name")
                : title === "holiday"
                ? t("Holiday Name")
                : title === "break"
                ? t("Break Name")
                : title === "levels"
                ? t("Level Name")
                : t("Department Name")
            }
            name="name"
            rules={[
              {
                required: true,
                message:
                  title === "Add Location"
                    ? t("Please enter location name")
                    : title === "holiday"
                    ? t("Please enter holiday name")
                    : title === "break"
                    ? t("Please enter break name")
                    : title === "levels"
                    ? t("Please enter level name")
                    : t("Please enter department name"),
              },
              {
                validator: (rule, value) => {
                  if (
                    value &&
                    !VALIDATE_CHARACTER.test(value) &&
                    title !== "holiday" &&
                    title !== "department"
                  ) {
                    return Promise.reject(
                      title === "Add Location"
                        ? t(
                            "Location Name should not contain special characters"
                          )
                        : title === "break"
                        ? t("Break Name should not contain special characters")
                        : title === "levels"
                        ? t("Level Name should not contain special characters")
                        : t(
                            "Department Name should not contain special characters"
                          )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={
                title === "Add Location"
                  ? t("Location Name")
                  : title === "holiday"
                  ? t("Holiday Name")
                  : title === "break"
                  ? t("Break Name")
                  : title === "levels"
                  ? t("Level Name")
                  : t("Department Name")
              }
              maxLength={30}
            />
          </Form.Item>

          {/* Location fields */}
          {title === "Add Location" && (
            <>
              <Form.Item
                name="usertimezone"
                label={t("Time Zone")}
                rules={[
                  { required: true, message: t("Please select Time zone") },
                ]}
              >
                <Select showSearch placeholder={t("Search to Select")}>
                  {TIMEZONELIST?.map((item, index) => (
                    <Select.Option key={index} value={item}>
                      {item}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={t("Latitude")}
                name="location_latitude"
                rules={[
                  {
                    required: true,
                    pattern: NON_CHARACTER,
                    message: t("Please enter latitude!"),
                  },
                ]}
              >
                <Input placeholder={t("Enter latitude")} maxLength={20} />
              </Form.Item>

              <Form.Item
                label={t("Longitude")}
                name="location_longitude"
                rules={[
                  {
                    required: true,
                    pattern: NON_CHARACTER,
                    message: t("Please enter longitude!"),
                  },
                ]}
              >
                <Input placeholder={t("Enter longitude")} maxLength={20} />
              </Form.Item>
            </>
          )}

          {/* Holiday */}
          {title === "holiday" && (
            <Row>
              <Col xs={24} ref={containerRef}>
                <Form.Item
                  label={t("Start date")}
                  name="start_date"
                  rules={[
                    {
                      required: true,
                      pattern: NON_EMPTY_REGEX,
                      message: t("Please enter start date!"),
                    },
                  ]}
                >
                  <DatePicker
                    className="w-100"
                    format="DD-MM-YYYY"
                    onChange={validateDateRange}
                    placeholder={t("Select start date")}
                    getPopupContainer={() => containerRef.current}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} ref={containerRef}>
                <Form.Item
                  label={t("End date")}
                  name="end_date"
                  rules={[
                    {
                      required: true,
                      pattern: NON_EMPTY_REGEX,
                      message: t("Please enter end date!"),
                    },
                    ({ getFieldValue }) => ({
                      validator(rule, value) {
                        const date = moment(value);
                        const startDate = moment(getFieldValue("start_date"));

                        if (date.isBefore(startDate, "day")) {
                          return Promise.reject(
                            t("End date cannot be before start date")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    className="w-100"
                    format="DD-MM-YYYY"
                    placeholder={t("Select end date")}
                    getPopupContainer={() => containerRef.current}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Description */}
          {title === "levels" && (
            <Form.Item
              label={t("Description")}
              name="description"
              rules={[
                { required: true, message: t("Please enter Description") },
              ]}
            >
              <Input placeholder={t("Enter Description")} maxLength={100} />
            </Form.Item>
          )}
        </div>
      </Form>
    </Styleddiv>
  );
};

const mapStateToProps = (state) => ({
  holidayList: getHolidayList(state),
});

export default connect(mapStateToProps, null)(EditInformation);
